import {
  Alert,
  Button,
  Form,
  Modal,
  message,
  Select,
  Skeleton,
  Table,
  Typography,
} from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Empty from '@/components/QuietEmpty';
import { DetailSectionPanel } from '@/modules/zy-crm/components/detail-section-panel';
import { MemberPicker } from '@/modules/zy-crm/components/member-picker';
import { useMemberDirectory } from '@/modules/zy-crm/member-directory';
import {
  type CrmScopedViewMode,
  resolveCrmScopedViewModeFromScope,
} from '@/modules/zy-crm/utils/crm-view';
import type { CrmPageScope } from '@/modules/zy-crm/utils/data-scope';
import { orgDirectoryAdapter } from '@/modules/zy-crm/utils/org-directory-adapter';
import {
  createContactTeamMember,
  deleteContactTeamMember,
  queryContactDrawerProfile,
} from '@/services/bff/contact.api';
import {
  createContractTeamMember,
  queryContractDrawerProfile,
  removeContractTeamMember,
} from '@/services/bff/contract.api';
import {
  createCustomerTeamMember,
  deleteCustomerTeamMember,
  queryCustomerDrawerProfile,
  updateCustomerTeamMemberRole,
} from '@/services/bff/customer.api';
import {
  type OptionSelectOption,
  queryOptionItemOptions,
} from '@/services/bff/dictionary-management.api';
import {
  createLeadTeamMember,
  deleteLeadTeamMember,
  queryLeadDrawerProfile,
} from '@/services/bff/lead.api';
import {
  createOpportunityTeamMember,
  deleteOpportunityTeamMember,
  queryOpportunityDrawerProfile,
} from '@/services/bff/opportunity.api';
import {
  createQuoteTeamMember,
  deleteQuoteTeamMember,
  queryQuoteDrawerProfile,
} from '@/services/bff/quote.api';
import type { ContactDrawerTeamMemberItem } from '@/services/bff/types/contact';
import type { ContractDrawerTeamMember } from '@/services/bff/types/contract';
import type { CustomerDrawerTeamMember } from '@/services/bff/types/customer';
import type { LeadDrawerTeamMember } from '@/services/bff/types/lead';
import type { OpportunityDrawerTeamMember } from '@/services/bff/types/opportunity';
import type { QuoteDrawerTeamMember } from '@/services/bff/types/quote';

const { Text, Paragraph } = Typography;
const MemberPickerField = MemberPicker as unknown as React.FC<any>;

export type TeamMembersPanelBizType =
  | 'contact'
  | 'customer'
  | 'lead'
  | 'opportunity'
  | 'contract'
  | 'quote';

type TeamMemberFormValues = {
  memberUuid?: string;
  roleCode?: string;
};

type TeamMemberPage<T> = {
  list: T[];
  total: number;
};

type NormalizedTeamMember = {
  id: string;
  uuid?: string;
  memberUuid?: string;
  memberName?: string;
  role?: string;
  roleCode?: string;
  roleName?: string;
  isOwner: boolean;
  raw: unknown;
};

type TeamMembersPanelProps = {
  bizType: TeamMembersPanelBizType;
  bizUuid: string;
  pageScope?: CrmPageScope;
  viewMode?: CrmScopedViewMode;
  className?: string;
  onRefresh?: () => void | Promise<void>;
};

type BizConfig<T> = {
  title: string;
  memberLabel: string;
  roleLabel: string;
  emptyText: string;
  emptyHint: string;
  description?: string;
  load: (
    bizUuid: string,
    pageScope?: 'self' | 'all',
    viewMode?: CrmScopedViewMode,
  ) => Promise<TeamMemberPage<T>>;
  normalize: (item: T) => NormalizedTeamMember;
  create?: (
    bizUuid: string,
    payload: Required<TeamMemberFormValues>,
  ) => Promise<unknown>;
  updateRole?: (
    bizUuid: string,
    item: NormalizedTeamMember,
    roleCode: string,
  ) => Promise<unknown>;
  remove?: (bizUuid: string, item: NormalizedTeamMember) => Promise<unknown>;
  buildHint: (total: number) => string;
};

const EMPTY_PAGE = {
  list: [],
  total: 0,
};

const getContactTeamMembers = async (
  bizUuid: string,
  pageScope?: 'self' | 'all',
  viewMode?: CrmScopedViewMode,
) => {
  const profile = await queryContactDrawerProfile(bizUuid, {
    viewMode: viewMode || resolveCrmScopedViewModeFromScope(pageScope),
  });
  return profile?.related?.teamMembers || EMPTY_PAGE;
};

const getCustomerTeamMembers = async (
  bizUuid: string,
  pageScope?: 'self' | 'all',
  viewMode?: CrmScopedViewMode,
) => {
  const profile = await queryCustomerDrawerProfile(bizUuid, {
    viewMode: viewMode || resolveCrmScopedViewModeFromScope(pageScope),
  });
  return profile?.related?.teamMembers || EMPTY_PAGE;
};

const getLeadTeamMembers = async (
  bizUuid: string,
  pageScope?: 'self' | 'all',
  _viewMode?: CrmScopedViewMode,
) => {
  const profile = await queryLeadDrawerProfile(bizUuid, {
    scope: pageScope,
  });
  return profile?.related?.teamMembers || EMPTY_PAGE;
};

const getOpportunityTeamMembers = async (
  bizUuid: string,
  pageScope?: 'self' | 'all',
  viewMode?: CrmScopedViewMode,
) => {
  const profile = await queryOpportunityDrawerProfile(bizUuid, {
    viewMode: viewMode || resolveCrmScopedViewModeFromScope(pageScope),
  });
  return profile?.related?.teamMembers || EMPTY_PAGE;
};

const getContractTeamMembers = async (
  bizUuid: string,
  pageScope?: 'self' | 'all',
  viewMode?: CrmScopedViewMode,
) => {
  const profile = await queryContractDrawerProfile(bizUuid, {
    viewMode: viewMode || resolveCrmScopedViewModeFromScope(pageScope),
  });
  return profile?.related?.teamMembers || EMPTY_PAGE;
};

const getQuoteTeamMembers = async (
  bizUuid: string,
  pageScope?: 'self' | 'all',
  viewMode?: CrmScopedViewMode,
) => {
  const profile = await queryQuoteDrawerProfile(bizUuid, {
    viewMode: viewMode || resolveCrmScopedViewModeFromScope(pageScope),
  });
  return profile?.related?.teamMembers || EMPTY_PAGE;
};

const BIZ_CONFIG: Record<TeamMembersPanelBizType, BizConfig<any>> = {
  contact: {
    title: '协作成员',
    memberLabel: '成员',
    roleLabel: '协作角色',
    emptyText: '',
    emptyHint: '共 0 位协作成员',
    load: getContactTeamMembers,
    normalize: (item: ContactDrawerTeamMemberItem) => ({
      id:
        item.uuid ||
        item.key ||
        item.memberUuid ||
        item.memberName ||
        'contact-team-member',
      uuid: item.uuid,
      memberUuid: item.memberUuid,
      memberName: item.memberName,
      role: item.role,
      roleCode: item.roleCode,
      roleName: item.roleName,
      isOwner: Boolean(item.isOwner),
      raw: item,
    }),
    create: async (bizUuid, payload) => {
      await createContactTeamMember(bizUuid, payload);
    },
    remove: async (bizUuid, item) => {
      if (!item.uuid) {
        return;
      }
      await deleteContactTeamMember(bizUuid, item.uuid);
    },
    buildHint: (total) =>
      total ? `共 ${total} 位协作成员` : '共 0 位协作成员',
  },
  customer: {
    title: '协作成员',
    memberLabel: '内部成员',
    roleLabel: '协作角色',
    emptyText: '',
    emptyHint: '记录参与该客户跟进的内部成员和分工',
    description: '这里记录参与该客户跟进的内部成员和分工，不含客户联系人。',
    load: getCustomerTeamMembers,
    normalize: (item: CustomerDrawerTeamMember) => ({
      id:
        item.uuid ||
        item.key ||
        item.memberUuid ||
        item.memberName ||
        'customer-team-member',
      uuid: item.uuid,
      memberUuid: item.memberUuid,
      memberName: item.memberName,
      role: item.role,
      roleCode: item.roleCode,
      roleName: item.roleName,
      isOwner: Boolean(item.isOwner),
      raw: item,
    }),
    create: async (bizUuid, payload) => {
      await createCustomerTeamMember(bizUuid, payload);
    },
    updateRole: async (bizUuid, item, roleCode) => {
      if (!item.uuid) {
        return;
      }
      await updateCustomerTeamMemberRole(bizUuid, item.uuid, { roleCode });
    },
    remove: async (bizUuid, item) => {
      if (!item.uuid) {
        return;
      }
      await deleteCustomerTeamMember(bizUuid, item.uuid);
    },
    buildHint: (total) =>
      total
        ? `共 ${total} 位内部协作成员`
        : '记录参与该客户跟进的内部成员和分工',
  },
  lead: {
    title: '协作成员',
    memberLabel: '内部成员',
    roleLabel: '协作角色',
    emptyText: '',
    emptyHint: '记录参与该线索跟进的内部成员和分工',
    description: '这里记录参与该线索跟进的内部成员和分工，不含客户联系人。',
    load: getLeadTeamMembers,
    normalize: (item: LeadDrawerTeamMember) => ({
      id: item.uuid || item.key || item.memberUuid || 'lead-team-member',
      uuid: item.uuid,
      memberUuid: item.memberUuid,
      memberName: item.memberName,
      role: item.role,
      roleCode: item.roleCode,
      roleName: item.roleName,
      isOwner: Boolean(item.isOwner),
      raw: item,
    }),
    create: async (bizUuid, payload) => {
      await createLeadTeamMember(bizUuid, payload);
    },
    remove: async (bizUuid, item) => {
      const record = item.raw as LeadDrawerTeamMember;
      if (!record.uuid) {
        return;
      }
      await deleteLeadTeamMember(bizUuid, record.uuid);
    },
    buildHint: (total) =>
      total
        ? `共 ${total} 位内部协作成员`
        : '记录参与该线索跟进的内部成员和分工',
  },
  opportunity: {
    title: '协作成员',
    memberLabel: '内部成员',
    roleLabel: '协作角色',
    emptyText: '',
    emptyHint: '记录参与该商机跟进的内部成员和分工',
    description: '这里记录参与该商机跟进的内部成员和分工，不含客户联系人。',
    load: getOpportunityTeamMembers,
    normalize: (item: OpportunityDrawerTeamMember) => ({
      id: item.uuid || item.key || item.memberUuid || 'opportunity-team-member',
      uuid: item.uuid,
      memberUuid: item.memberUuid,
      memberName: item.memberName,
      role: item.role,
      roleCode: item.roleCode,
      roleName: item.roleName,
      isOwner: Boolean(item.isOwner),
      raw: item,
    }),
    create: async (bizUuid, payload) => {
      await createOpportunityTeamMember(bizUuid, payload);
    },
    remove: async (bizUuid, item) => {
      const record = item.raw as OpportunityDrawerTeamMember;
      if (!record.uuid) {
        return;
      }
      await deleteOpportunityTeamMember(bizUuid, record.uuid);
    },
    buildHint: (total) =>
      total
        ? `共 ${total} 位内部协作成员`
        : '记录参与该商机跟进的内部成员和分工',
  },
  contract: {
    title: '协作成员',
    memberLabel: '成员',
    roleLabel: '协作角色',
    emptyText: '',
    emptyHint: '共 0 位协作成员',
    load: getContractTeamMembers,
    normalize: (item: ContractDrawerTeamMember) => ({
      id: item.uuid || item.key || item.memberUuid || 'contract-team-member',
      uuid: item.uuid,
      memberUuid: item.memberUuid,
      memberName: item.memberName,
      role: item.role,
      roleCode: item.roleCode,
      roleName: item.roleName,
      isOwner: Boolean(item.isOwner),
      raw: item,
    }),
    create: async (bizUuid, payload) => {
      await createContractTeamMember(bizUuid, payload);
    },
    remove: async (bizUuid, item) => {
      const record = item.raw as ContractDrawerTeamMember;
      if (!record.uuid) {
        return;
      }
      await removeContractTeamMember(bizUuid, record.uuid);
    },
    buildHint: (total) =>
      total ? `共 ${total} 位协作成员` : '共 0 位协作成员',
  },
  quote: {
    title: '协作成员',
    memberLabel: '成员',
    roleLabel: '协作角色',
    emptyText: '',
    emptyHint: '共 0 位协作成员',
    load: getQuoteTeamMembers,
    normalize: (item: QuoteDrawerTeamMember) => ({
      id: item.uuid || item.key || item.memberUuid || 'quote-team-member',
      uuid: item.uuid,
      memberUuid: item.memberUuid,
      memberName: item.memberName,
      role: item.role,
      roleCode: item.roleCode,
      roleName: item.roleName,
      isOwner: Boolean(item.isOwner),
      raw: item,
    }),
    create: async (bizUuid, payload) => {
      await createQuoteTeamMember(bizUuid, payload);
    },
    remove: async (bizUuid, item) => {
      const record = item.raw as QuoteDrawerTeamMember;
      if (!record.uuid) {
        return;
      }
      await deleteQuoteTeamMember(bizUuid, record.uuid);
    },
    buildHint: (total) =>
      total ? `共 ${total} 位协作成员` : '共 0 位协作成员',
  },
};

function resolveMemberName(
  item: NormalizedTeamMember,
  memberNameMap: Record<string, string>,
) {
  if (item.memberName) {
    return item.memberName;
  }
  if (item.memberUuid && memberNameMap[item.memberUuid]) {
    return memberNameMap[item.memberUuid];
  }
  return item.memberUuid || '未命名成员';
}

function resolveRoleName(
  item: NormalizedTeamMember,
  roleNameMap: Record<string, string>,
) {
  if (item.roleName) {
    return item.roleName;
  }
  if (item.roleCode && roleNameMap[item.roleCode]) {
    return roleNameMap[item.roleCode];
  }
  if (item.role) {
    return item.role;
  }
  return item.roleCode || '';
}

export function TeamMembersPanel({
  bizType,
  bizUuid,
  pageScope = 'self',
  viewMode,
  className,
  onRefresh,
}: TeamMembersPanelProps) {
  const config = BIZ_CONFIG[bizType];
  const { memberNameMap } = useMemberDirectory();
  const [items, setItems] = useState<NormalizedTeamMember[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<Error>();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<NormalizedTeamMember>();
  const [submitting, setSubmitting] = useState(false);
  const [roleOptions, setRoleOptions] = useState<OptionSelectOption[]>([]);
  const [roleOptionsLoading, setRoleOptionsLoading] = useState(false);
  const [roleOptionsError, setRoleOptionsError] = useState<string>();
  const [form] = Form.useForm<TeamMemberFormValues>();

  const canCreate = Boolean(config.create);
  const canEditRole = Boolean(config.updateRole);
  const roleNameMap = useMemo(
    () =>
      roleOptions.reduce<Record<string, string>>((acc, item) => {
        if (item.code && item.label) {
          acc[item.code] = item.label;
        }
        return acc;
      }, {}),
    [roleOptions],
  );

  const loadRoleOptions = useCallback(async () => {
    setRoleOptionsLoading(true);
    setRoleOptionsError(undefined);
    try {
      const list = await queryOptionItemOptions('crm.team_member_role');
      setRoleOptions(list);
    } catch (error) {
      const nextError =
        error instanceof Error ? error.message : '协作角色选项加载失败';
      setRoleOptions([]);
      setRoleOptionsError(nextError);
      message.error(nextError);
    } finally {
      setRoleOptionsLoading(false);
    }
  }, []);

  const loadData = useCallback(async () => {
    if (!bizUuid) {
      setItems([]);
      setTotal(0);
      setLoadError(undefined);
      return;
    }
    setLoading(true);
    setLoadError(undefined);
    try {
      const page = await config.load(bizUuid, pageScope, viewMode);
      const nextItems = (page.list || []).map((item) => config.normalize(item));
      setItems(nextItems);
      setTotal(page.total || nextItems.length);
    } catch (error) {
      const nextError =
        error instanceof Error ? error : new Error('协作成员加载失败');
      setLoadError(nextError);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [bizUuid, config, pageScope, viewMode]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (!canCreate) {
      return;
    }
    void loadRoleOptions();
  }, [canCreate, loadRoleOptions]);

  const openCreateModal = useCallback(() => {
    form.resetFields();
    setCreateOpen(true);
    if (!roleOptions.length && !roleOptionsLoading) {
      void loadRoleOptions();
    }
  }, [form, loadRoleOptions, roleOptions.length, roleOptionsLoading]);

  const closeCreateModal = useCallback(() => {
    setCreateOpen(false);
    setEditingItem(undefined);
    form.resetFields();
  }, [form]);

  const openEditModal = useCallback(
    (item: NormalizedTeamMember) => {
      if (!config.updateRole) {
        return;
      }
      form.setFieldsValue({ roleCode: item.roleCode });
      setEditingItem(item);
      setCreateOpen(true);
      if (!roleOptions.length && !roleOptionsLoading) {
        void loadRoleOptions();
      }
    },
    [
      config.updateRole,
      form,
      loadRoleOptions,
      roleOptions.length,
      roleOptionsLoading,
    ],
  );

  const handleCreate = useCallback(async () => {
    if (!bizUuid) {
      return;
    }
    const values = await form.validateFields();
    const roleCode = String(values.roleCode || '').trim();
    setSubmitting(true);
    try {
      if (editingItem && config.updateRole) {
        await config.updateRole(bizUuid, editingItem, roleCode);
        message.success('协作成员角色已更新');
      } else if (config.create) {
        await config.create(bizUuid, {
          memberUuid: String(values.memberUuid || '').trim(),
          roleCode,
        });
        message.success('协作成员已新增');
      }
      closeCreateModal();
      await loadData();
      await onRefresh?.();
    } finally {
      setSubmitting(false);
    }
  }, [
    bizUuid,
    closeCreateModal,
    config,
    editingItem,
    form,
    loadData,
    onRefresh,
  ]);

  const handleDelete = useCallback(
    (item: NormalizedTeamMember) => {
      if (!config.remove || !bizUuid) {
        return;
      }
      Modal.confirm({
        closable: { 'aria-label': '关闭' },
        keyboard: true,
        title: '确认删除该协作成员？',
        onOk: async () => {
          await config.remove?.(bizUuid, item);
          message.success('协作成员已删除');
          await loadData();
          await onRefresh?.();
        },
      });
    },
    [bizUuid, config, loadData, onRefresh],
  );

  const columns = useMemo(
    () => [
      {
        title: config.memberLabel,
        dataIndex: 'memberName',
        key: 'memberName',
        render: (_: unknown, item: NormalizedTeamMember) =>
          resolveMemberName(item, memberNameMap),
      },
      {
        title: config.roleLabel,
        dataIndex: 'role',
        key: 'role',
        render: (_: unknown, item: NormalizedTeamMember) =>
          resolveRoleName(item, roleNameMap),
      },
      {
        title: '操作',
        key: 'actions',
        width: 160,
        render: (_: unknown, item: NormalizedTeamMember) =>
          !item.isOwner && item.uuid ? (
            <>
              {config.updateRole ? (
                <Button
                  type="link"
                  size="small"
                  onClick={() => openEditModal(item)}
                >
                  编辑
                </Button>
              ) : null}
              {config.remove ? (
                <Button
                  type="link"
                  size="small"
                  danger
                  onClick={() => handleDelete(item)}
                >
                  删除
                </Button>
              ) : null}
            </>
          ) : null,
      },
    ],
    [config, handleDelete, memberNameMap, openEditModal, roleNameMap],
  );

  const content = useMemo(() => {
    if (loadError) {
      return (
        <Alert
          type="error"
          showIcon
          message="协作成员加载失败"
          description={loadError.message}
          action={
            <Button size="small" onClick={() => void loadData()}>
              重试
            </Button>
          }
        />
      );
    }
    if (loading) {
      return <Skeleton active paragraph={{ rows: 4 }} />;
    }
    return (
      <Table
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={items}
        locale={{ emptyText: <Empty /> }}
        pagination={false}
      />
    );
  }, [columns, config.emptyText, items, loadData, loadError, loading]);

  return (
    <>
      <DetailSectionPanel
        title={config.title}
        hint={config.buildHint(total) || config.emptyHint}
        extra={
          canCreate ? (
            <Button type="link" size="small" onClick={openCreateModal}>
              新增协作成员
            </Button>
          ) : null
        }
        className={className}
      >
        {config.description ? (
          <Paragraph type="secondary" style={{ marginBottom: 12 }}>
            {config.description}
          </Paragraph>
        ) : null}
        {content}
      </DetailSectionPanel>
      {canCreate || canEditRole ? (
        <Modal
          closable={{ 'aria-label': '关闭' }}
          keyboard
          open={createOpen}
          title={editingItem ? '编辑协作角色' : '新增协作成员'}
          onCancel={closeCreateModal}
          onOk={() => void handleCreate()}
          confirmLoading={submitting}
          destroyOnHidden
          forceRender
        >
          <Form form={form} layout="vertical">
            {!editingItem ? (
              <Form.Item
                name="memberUuid"
                label={config.memberLabel}
                rules={[
                  { required: true, message: `请选择${config.memberLabel}` },
                ]}
              >
                <MemberPickerField adapter={orgDirectoryAdapter} />
              </Form.Item>
            ) : null}
            <Form.Item
              name="roleCode"
              label={config.roleLabel}
              rules={[{ required: true, message: `请选择${config.roleLabel}` }]}
              extra={
                roleOptionsError ? (
                  <Text type="danger">{roleOptionsError}</Text>
                ) : undefined
              }
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="请选择协作角色"
                loading={roleOptionsLoading}
                options={roleOptions
                  .filter(
                    (item) =>
                      !editingItem ||
                      (item.code || item.value) !== 'owner' ||
                      editingItem.roleCode === 'owner',
                  )
                  .map((item) => ({
                    label: item.label,
                    value: item.code || item.value,
                    disabled:
                      Boolean(editingItem) &&
                      (item.code || item.value) === 'owner',
                  }))}
                disabled={roleOptionsLoading || roleOptions.length === 0}
              />
            </Form.Item>
          </Form>
        </Modal>
      ) : null}
    </>
  );
}
